import {isDebugModeEnabled} from "@/app/Features";
import VuexStore from "@/store/vuex/VuexStore";
import {getBrowserId} from "@/app/utils/Util";
import {grouse} from "@/store/Services";
import {currentAccountCode} from "@/app/utils/Account";
import {
    earliestDate,
    getBrandsInFilter,
    getPlatformFromFilter,
    getTagsInFilter,
    latestDate
} from "@/dashboards/filter/FilterParser";


/**
 * These are calls that have been queued up for grouse.
 * We do not want to overload grouse, especially when using
 * http/2 multiplexing.
 *
 * @see drainGrouseQueue
 * @type {{task: Function, resolve: Function, reject: Function}[]}
 */
const grouseCalls = [];
let runningGrouseCalls = 0;

/**
 * The maximum number of concurrent calls to grouse that we are allowing to be made.
 * @type {number}
 */
const MAX_ALLOWED = 12;


/**
 * Starts waiting calls in queue order whenever a slot is available.
 * Request failures belong to their callers, not to queue scheduling.
 */
function drainGrouseQueue() {
    while (grouseCalls.length && runningGrouseCalls < MAX_ALLOWED) {
        const record = grouseCalls.shift();
        // Reserve the slot before invoking the task: promise executors run synchronously.
        runningGrouseCalls++;
        new Promise(record.task)
            .then(record.resolve, record.reject)
            .finally(() => {
                runningGrouseCalls--;
                drainGrouseQueue();
            });
    }
}

/**
 * Queues a task and returns only that request's result.
 * @param grouseCall {Function} Promise executor, invoked only when a slot is available.
 * @return {Promise<*>}
 */
function recordAndExecute(grouseCall) {
    return new Promise((resolve, reject) => {
        grouseCalls.push({ task: grouseCall, resolve, reject });
        drainGrouseQueue();
    });
}



/**
 * Get some JSON data from Grouse and returns a cancellable promise.
 * THIS DOES NOT SET ANY CACHE TOKEN. You need to set this yourself.
 *
 * @param endpoint {string}
 * @param [params = {}] {Object} Url parameters you wish to pass through. Should not be url encoded.
 * @param abortController {AbortController?} If you want to pass in your own abort controller for cancelling requests.
 * @return {Promise<AxiosResponse<any>>}
 */
export function grouseGet(endpoint, params = {}, abortController = new AbortController()) {

    let promise = recordAndExecute((resolve, reject) => {
        try {
            grouse
                .get(endpoint, { params, signal: abortController.signal })
                .then(response => resolve(response.data))
                .catch(e => {
                    reject(e);
                });
        } catch (e) {
            reject(e);
        }
    });

    promise.cancel = () => abortController?.abort();
    return promise;
}

/**
 * Returns a particular mention for an account. This is enough information to show in the MentionItemView as well.
 * This is a cancellable promise, like so: getMention(url).cancel();
 * @param code {String}
 * @param id {String}
 * @param [withCache=true] {Boolean, optional} defaults to true.
 */
export function getMention(code, id, withCache) {
    withCache = withCache === undefined ? true : withCache;


    const url = '/v4/accounts/' + code + "/mentions/" + id;

    const params = {
        useAccountTimeZone: true,
        select: "+combinedHtml,phrases,brand,discardedByRule,region,crowdJobs",
    };

    if (withCache) params.cacheToken = getBrowserId();
    if (isDebugModeEnabled()) params.debug=true;

    return grouseGet(url, params);
}

/**
 * Fetch a list of mentions.
 * @param {FilterString} filter
 * @param {Object?} orderBy
 * @param {Object?} limit
 * @param [multiBrand = false]
 * @param [noCache = false]
 * @param {Object} [options] Optional accountCode, select and abortController overrides.
 * @returns {Promise<unknown>} Cancellable, as with grouseGet.
 */
export function getMentions(filter, orderBy, limit= 30, multiBrand = false, noCache = false, options = {}) {
    const code = options.accountCode ?? VuexStore.state.account.code;
    if (!code) throw new Error("No account code supplied");
    if (!filter) throw new Error("No filter supplied");

    const url = '/v4/accounts/' + code + "/mentions";

    const params = {
        useAccountTimeZone: true,
        filter: filter,
        limit:  limit,
        select: options.select ?? "+combinedHtml,phrases,brand,discardedByRule,region,crowdJobs"
    };

    if (isDebugModeEnabled()) params.debug = true;
    if (!noCache) params.cacheToken = getBrowserId();
    if (multiBrand) params.multiBrand = true;
    if (orderBy && orderBy.length) params.orderBy = orderBy.join(',');

    return grouseGet(url, params, options.abortController);
}




/**
 *
 * @param filter {String}
 * @param groupBy {Array,optional}
 * @param select {Array,optional}
 * @param limit {Number,optional}
 * @param params {Object,optional}
 * @param code {String,optional}
 * @param {boolean} [useCache = true]
 * @returns {Promise<unknown>}
 */
export function count(filter, groupBy, select, limit, params, code, useCache) {
    code ??= currentAccountCode();
    useCache ??= true;

    params = {...params};
    params.filter = filter;
    if (useCache) params.cacheToken = getBrowserId();
    if (isDebugModeEnabled()) params.debug = true;
    if (groupBy && groupBy.length) params.groupBy = groupBy.join(','); //groupBy.map(encodeURIComponent).join(',');
    if (select && select.length) params.select = select.join(','); // select.map(encodeURIComponent).join(',');
    if (limit) params.limit = limit;

    const signal = new AbortController();

    let promise = recordAndExecute((resolve, reject) => {
        grouse.get("/v4/accounts/" + code + "/mentions/count", { params, signal: signal.signal })
            .then(response => resolve(response.data))
            .catch(e => reject(e))
    });

    promise.cancel = () => signal.abort();
    return promise;
}

/**
 *
 * @param filter {String}
 * @param groupBy {String, optional}
 * @param select {String []}
 * @param limit {Number,optional}
 * @param payload {Object,optional}
 * @param code {String,optional}
 * @returns {Promise<unknown>}
 */
export function countTickets(filter, groupBy,select , limit, payload, code) {
    code ??= currentAccountCode();

    payload = {...payload};

    const brands = getBrandsInFilter(filter);
    const earliestDateParam = earliestDate(filter, true);
    const latestDateParam = latestDate(filter, true);
    const tagsInFilter = getTagsInFilter(filter);
    const socialNetworks = getPlatformFromFilter(filter);

    payload = {
        ...payload,
        brandIds: brands.include,
        firstMentionPublished: earliestDateParam.toISOString(),
        lastMentionPublished: latestDateParam.toISOString(),
        includedTagIds: tagsInFilter.include,
        excludedTagIds: tagsInFilter.exclude,
        includedSocialNetworks: socialNetworks,
        groupBy: groupBy,
        select: select
    }

    if (isDebugModeEnabled()) payload.debug = true;
    if (limit) payload.limit = limit;

    const signal = new AbortController();

    let promise = recordAndExecute((resolve, reject) => {
        grouse.post("/v4/accounts/" + code + "/tickets/count", payload,{ signal: signal.signal })
            .then(response => resolve(response.data))
            .catch(e => reject(e))
    });

    promise.cancel = () => signal.abort();
    return promise;
}

/**
 * Create a link to a Grouse endpoint.
 */
export function toGrouseLink(endpoint, data, noAuth) {
    // noinspection JSUnresolvedVariable
    const grouseUrl = Beef?.startupOptions?.grouseUrl;

    // proxy via beef if auth is required e.g. CSV download link
    let link = noAuth ? grouseUrl + endpoint : "/api" + endpoint;
    let first = true;
    if (data) {
        Object.entries(data).forEach(([key, value]) => {
            if (value) {
                if (first) {
                    link += "?";
                    first = false;
                } else {
                    link += "&";
                }
                link += key + "=" + encodeURIComponent(value);
            }
        });
    }
    return link;
}

export async function grousePut(endpoint, data = {}) {
    if (isDebugModeEnabled()) data.debug = true;

    const response = await grouse.put(endpoint, data);
    return response.data;
}
